import { useState } from "react";
import { put } from "@common/api/apiShared";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import { useDotLegalMutation, useDotLegalQuery, useDotLegalQueryClient } from "@common/hooks/dotLegalReactQuery";
import useCountryCurrencyHook, { defaultCurrencyCode } from "@common/hooks/useCountryCurrencyList";
import { RiskGlobalSettingViewModel } from "./GlobalSettings.types";
import { RiskExposureMode } from "../../risk/riskExposure/RiskExposure.types";

const url = "/risk-global-settings";
const defaultScaleUpperBound = 10;
const minimumScaleUpperBound = 2;

export interface IGlobalSettingsDialogProps {
    onDialogClose: () => void;
}

export function useGlobalSettingsHook(props: IGlobalSettingsDialogProps) {
    const queryClient = useDotLegalQueryClient();
    const snackbar = useDotLegalSnackbar();
    const { translateString } = useTranslation();
    const { getCurrenciesAsIsoCodes } = useCountryCurrencyHook();
    const { isLoading, data } = useDotLegalQuery<RiskGlobalSettingViewModel>(url);

    const [editedValue, setEditedValue] = useState<string | undefined>(undefined);
    const value = editedValue ?? data?.maxRisksOverAppetite?.toString() ?? "";

    const [editedExposureMode, setEditedExposureMode] = useState<RiskExposureMode | undefined>(undefined);
    const [editedCurrencyCode, setEditedCurrencyCode] = useState<string | null | undefined>(undefined);
    const [editedScaleUpperBound, setEditedScaleUpperBound] = useState<string | undefined>(undefined);
    const [editedShowLegacyRiskAssessments, setEditedShowLegacyRiskAssessments] = useState<boolean | undefined>(undefined);

    const exposureMode = editedExposureMode ?? data?.exposureMode ?? RiskExposureMode.None;
    const currencyCode = editedCurrencyCode !== undefined ? editedCurrencyCode : (data?.exposureCurrencyCode ?? defaultCurrencyCode);
    const scaleUpperBound = editedScaleUpperBound ?? data?.exposureScaleUpperBound?.toString() ?? defaultScaleUpperBound.toString();
    const showLegacyRiskAssessments = editedShowLegacyRiskAssessments ?? data?.showLegacyRiskAssessments ?? true;

    const parsed = parseInt(value, 10);
    const parsedUpperBound = parseInt(scaleUpperBound, 10);
    const appetiteValid = !isNaN(parsed) && parsed >= 0;
    // Both modes require a currency — in Scale mode it denominates mitigation costs.
    const exposureValid =
        exposureMode === RiskExposureMode.None ||
        (!!currencyCode && (exposureMode === RiskExposureMode.Financial || (!isNaN(parsedUpperBound) && parsedUpperBound >= minimumScaleUpperBound)));
    const saveEnabled = appetiteValid && exposureValid;

    const savedExposureConfigured = (data?.exposureMode ?? RiskExposureMode.None) !== RiskExposureMode.None;
    const exposureConfigChanged =
        savedExposureConfigured &&
        (exposureMode !== data!.exposureMode ||
            (exposureMode !== RiskExposureMode.None && currencyCode !== data!.exposureCurrencyCode) ||
            (exposureMode === RiskExposureMode.Scale && parsedUpperBound !== data!.exposureScaleUpperBound));

    const saveMutation = useDotLegalMutation(save);
    const onSave = async () => {
        await saveMutation.mutateAsync(undefined, {
            onSuccess: () => {
                queryClient.refetchQueries(url);
                queryClient.refetchQueries("/risk-global-settings/exposure-config");
                // The menu reads the legacy visibility off /userContext, which is cached for an hour,
                // so without this the admin would not see their own change until the cache expired.
                queryClient.refetchQueries("/userContext");
                props.onDialogClose();
                snackbar.show(translateString("globalSettingsSaved"));
            },
        });
    };

    function save() {
        return put(url, {
            maxRisksOverAppetite: parsed,
            exposureMode,
            exposureCurrencyCode: exposureMode !== RiskExposureMode.None ? currencyCode : null,
            exposureScaleUpperBound: exposureMode === RiskExposureMode.Scale ? parsedUpperBound : null,
            showLegacyRiskAssessments,
        });
    }

    const exposureModeOptions = [
        { id: RiskExposureMode.None.toString(), name: translateString("none") },
        { id: RiskExposureMode.Financial.toString(), name: translateString("riskExposureModeFinancial") },
        { id: RiskExposureMode.Scale.toString(), name: translateString("riskExposureModeScale") },
    ];

    const risksOverAppetite = data?.risksOverAppetite ?? 0;
    const isExceeded = appetiteValid ? risksOverAppetite > parsed : false;

    return {
        isLoading,
        value,
        onValueChange: setEditedValue,
        onSave,
        saveEnabled,
        inProgress: saveMutation.isLoading,
        risksOverAppetite,
        isExceeded,
        exposureMode,
        onExposureModeChange: (mode: string | null) => setEditedExposureMode(mode === null ? RiskExposureMode.None : Number(mode)),
        exposureModeOptions,
        currencyCode,
        onCurrencyCodeChange: setEditedCurrencyCode,
        currencyOptions: getCurrenciesAsIsoCodes(),
        scaleUpperBound,
        onScaleUpperBoundChange: setEditedScaleUpperBound,
        exposureConfigChanged,
        showLegacyRiskAssessments,
        onShowLegacyRiskAssessmentsChange: setEditedShowLegacyRiskAssessments,
    };
}
